import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import apiClient from '../../utils/apiClient';
import { toast } from 'sonner';
import { isIOSNative } from '../../utils/nativeBridge';

/**
 * iter959 Phase 3 - RetryBacklogButton
 *
 * Renders a "Retry Backlog" CTA under a bank tile whenever the latest
 * backlog_job for that bank_account_id ended in `charge_failed`. Clicking it
 * kicks off POST /api/banking/mono/{id}/retry-backlog and the persistent
 * BacklogProgressBanner takes over from there.
 *
 * Deliberately quiet when there's nothing to retry - returns null.
 */
export default function RetryBacklogButton({ bankAccountId, bankName = 'Bank', provider = 'mono' }) {
  // iOS App Store build: payment-retry UI must never render in-app.
  if (isIOSNative()) return null;
  return <RetryBacklogButtonInner bankAccountId={bankAccountId} bankName={bankName} provider={provider} />;
}

function RetryBacklogButtonInner({ bankAccountId, bankName = 'Bank', provider = 'mono' }) {
  const [state, setState] = useState({ loading: true, needsRetry: false, fee: null, currency: null, error: null });
  const [submitting, setSubmitting] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/banking/backlog-jobs/active');
      const jobs = data?.jobs || [];
      const forThisBank = jobs
        .filter((j) => j.bank_account_id === bankAccountId)
        .sort((a, b) => (b.started_at || '').localeCompare(a.started_at || ''));
      const latest = forThisBank[0];
      if (latest && (latest.phase === 'charge_failed' || latest.phase === 'post_failed')) {
        setState({
          loading: false, needsRetry: true,
          fee: latest.fee, currency: latest.currency, error: latest.error,
          jobId: latest.id, phase: latest.phase,
        });
      } else {
        setState({ loading: false, needsRetry: false });
      }
    } catch (_) {
      setState({ loading: false, needsRetry: false });
    }
  }, [bankAccountId]);

  useEffect(() => {
    loadStatus();
    // Re-check every 30s in case the user is on this page while a job flips
    const t = setInterval(loadStatus, 30000);
    return () => clearInterval(t);
  }, [loadStatus]);

  const retry = async () => {
    setSubmitting(true);
    try {
      const endpoint = provider === 'plaid'
        ? `/banking/plaid/${bankAccountId}/retry-backlog`
        : `/banking/mono/${bankAccountId}/retry-backlog`;
      const { data } = await apiClient.post(endpoint);
      if (data.already_in_progress || data.already_settled) {
        toast.info('A backlog job is already running: watch the banner at the top.');
      } else if (data.payment_failed) {
        toast.error(data.reason || 'Card charge failed again. Please update your card and retry.');
      } else {
        toast.success('Retrying backlog fetch…', {
          description: 'You can leave this page. The progress banner will keep you posted.',
        });
      }
      // Ack the old failed job so the button disappears; the banner will
      // pick up the fresh job on its next poll.
      if (state.jobId) {
        try { await apiClient.post(`/banking/backlog-jobs/${state.jobId}/ack`); } catch (_) { /* ignore */ }
      }
      setState({ loading: false, needsRetry: false });
    } catch (e) {
      toast.error('Could not start retry. Please try again.');
    } finally { setSubmitting(false); }
  };

  if (state.loading || !state.needsRetry) return null;

  const symbol = state.currency === 'NGN' ? '₦' : state.currency === 'GBP' ? '£' : '$';
  const feeCopy = state.fee != null ? ` (${symbol}${Number(state.fee).toLocaleString()})` : '';
  const isPostFailed = state.phase === 'post_failed';
  const title = isPostFailed
    ? `Backlog fee charged, but posting failed for ${bankName}${feeCopy}`
    : `Backlog fee failed for ${bankName}${feeCopy}`;
  const body = isPostFailed
    ? 'Card was charged. Retry to finish importing the historical transactions - you will not be charged again.'
    : 'Opening balance was posted and current-month transactions are live. Retry to pull the backlog.';

  return (
    <div
      className="mt-2 border border-amber-300 bg-amber-50 rounded-md p-2.5 flex items-center gap-2 text-sm"
      data-testid={`retry-backlog-card-${bankAccountId}`}
    >
      <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
      <div className="flex-1 text-amber-900">
        <div className="font-semibold">{title}</div>
        <div className="text-xs opacity-80">{body}</div>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={retry}
        disabled={submitting}
        className="border-amber-400 text-amber-900 hover:bg-amber-100"
        data-testid={`retry-backlog-btn-${bankAccountId}`}
      >
        {submitting ? (
          <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Starting…</>
        ) : (
          <><RefreshCw className="w-3.5 h-3.5 mr-1" /> Retry Backlog</>
        )}
      </Button>
    </div>
  );
}
